import {
  extractReadingValueFromSensorEvent,
  LeakDetectorReading,
  SensorType,
  WaterQualityColor,
  useSensorAlerts,
} from 'common';
import { useState, useEffect, useMemo } from 'react';

import { Status } from '../../types/Status';
import { useLatestSensorEvents } from '../sensors/useLatestSensorEvent';
import { useGetWaterQualityData } from '../water/useGetWaterQualityData';

interface WaterQualityData {
  status: Status;
  quality: string;
  statusLabel: string;
  humidity?: string;
  leaks: number;
}

export const useWaterQualitySensorStatus = (): WaterQualityData => {
  const [waterQualityData, setWaterQualityData] = useState<WaterQualityData>({
    status: Status.Good,
    quality: '',
    statusLabel: 'Good',
    leaks: 0,
  });

  const waterQualityStatus = useGetWaterQualityData();
  const event = useLatestSensorEvents(SensorType.LEAK_DETECTOR).event;
  const humidityValue =
    (event ? extractReadingValueFromSensorEvent<LeakDetectorReading>(event)?.humidity : null) ?? 0;
  const leak = event ? extractReadingValueFromSensorEvent<LeakDetectorReading>(event)?.leak : null;

  const { sensorAlerts, loading: _alertsLoading } = useSensorAlerts(event?.sensorId);

  const hasActiveLeakAlert = useMemo(() => {
    if (!sensorAlerts || sensorAlerts.length === 0) return false;

    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    return sensorAlerts.some(
      (alert) =>
        (alert.status === 'new' || alert.status === 'ongoing') &&
        alert.alertType.includes('LEAK') &&
        alert.acknowledgedAt === null &&
        alert.acknowledgedBy === null &&
        // Only consider alerts from the last 24 hours
        alert.timestamp &&
        alert.timestamp * 1000 >= twentyFourHoursAgo,
    );
  }, [sensorAlerts]);

  useEffect(() => {
    const formattedHumidity =
      humidityValue !== null ? `${Math.round(Math.min(Math.max(0, humidityValue), 100))}%` : 'N/A';

    const leakStatus = hasActiveLeakAlert || (leak && leak > 0);

    if (waterQualityStatus) {
      let status: Status = Status.Good;
      if (leakStatus) {
        status = Status.Error;
      } else {
        switch (waterQualityStatus.color) {
          case WaterQualityColor.Red:
            status = Status.Error;
            break;
          case WaterQualityColor.Yellow:
            status = Status.Poor;
            break;
          default:
            status = Status.Good;
        }
      }

      setWaterQualityData({
        status,
        quality: 'waterQuality',
        statusLabel: waterQualityStatus.label,
        humidity: formattedHumidity,
        leaks: leakStatus ? 1 : 0,
      });
    } else {
      setWaterQualityData({
        status: leakStatus ? Status.Error : Status.Good,
        quality: 'waterQuality',
        statusLabel: 'Good',
        leaks: leakStatus ? 1 : 0,
      });
    }
  }, [waterQualityStatus?.color, humidityValue, hasActiveLeakAlert, leak]);

  return waterQualityData;
};
